import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

const Sidebar = ({
  projects,
  onSelectProject,
  onAddProject,
  filterRoute,
  selectedProjectId,
}) => {
  const [newProject, setNewProject] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [hoveredId, setHoveredId] = useState(null);
  const [showArchived, setShowArchived] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const pathname = location.pathname;
  let currentFilter;

  if (pathname === "/projects") {
    currentFilter = "/after-booking";
  } else if (["/before-booking", "/after-booking"].includes(pathname)) {
    currentFilter = pathname;
  } else {
    currentFilter = filterRoute || "/before-booking";
  }

  const isBeforeBooking = currentFilter === "/before-booking";

  const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);

  const removeProjectNo = (name = "") => {
    return name.replace(/^\d{2}-\d{4}-\d{2}[_\s-]+/, "").trim();
  };

  const filteredProjects = projects
    .filter((p) => {
      const hasPayment = !!p.hasPayment || Number(p.totalPaid || 0) >= 1;

      const lastUpdated = new Date(
        p.lastActivityAt ||
          p.lastUpdated ||
          p.updatedAt ||
          p.createdAt ||
          Date.now(),
      );

      const isInactive = lastUpdated < thirtyDaysAgo;

      const isClosed = p.isClosed === true;

      if (isBeforeBooking) {
        if (showArchived) {
          return !hasPayment && (p.isArchived || isInactive);
        }

        return !hasPayment && !p.isArchived && !isInactive;
      }

      if (currentFilter === "/after-booking") {
        if (showArchived) {
          return hasPayment && (isClosed || p.isArchived);
        }

        return hasPayment && !isClosed && !p.isArchived;
      }

      return false;
    })
    .filter((p) => p.name?.toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a, b) => (a.name || "").localeCompare(b.name || ""));

  const handleAddProject = () => {
    const trimmed = newProject.trim();

    if (trimmed === "") return;

    const exists = projects.some(
      (p) => removeProjectNo(p.name).toLowerCase() === trimmed.toLowerCase(),
    );

    if (exists) {
      alert("Project already exists");
      return;
    }

    const payload = {
      name: trimmed,
      address: "Not Provided",
    };

    onAddProject(payload);
    setNewProject("");
  };

  const handleProjectSelect = (project) => {
    onSelectProject(project, currentFilter);
    navigate("/project");
  };

  const isFinanceActive = pathname.toLowerCase().startsWith("/finance");

  // ============================================================
  // FINANCE PERMISSION
  // Only these roles can see Finance:
  // admin, head_accountant, accountant
  // ============================================================

  let loggedInUser = {};

  try {
    loggedInUser = JSON.parse(localStorage.getItem("user") || "{}");
  } catch (error) {
    loggedInUser = {};
  }

  const userRole = String(
    loggedInUser?.role ||
      loggedInUser?.roleName ||
      localStorage.getItem("userRole") ||
      "",
  )
    .trim()
    .toLowerCase();

  const canViewFinance = ["admin", "head_accountant", "accountant"].includes(
    userRole,
  );

  return (
    <div style={styles.sidebar}>
      {/* Search */}
      <div style={styles.searchContainer}>
        <input
          type="text"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={styles.searchInput}
        />

        <span style={styles.searchIcon}>🔍</span>
      </div>

      {/* Add Project */}
      {isBeforeBooking && !showArchived && (
        <div style={styles.addContainer}>
          <input
            type="text"
            value={newProject}
            onChange={(e) => setNewProject(e.target.value)}
            placeholder="Enter Project Name"
            style={styles.input}
          />

          <button onClick={handleAddProject} style={styles.addButton}>
            Add
          </button>
        </div>
      )}

      {/* Selected Project */}
      {selectedProjectId && (
        <div style={styles.selectedProject}>
          📌 <strong>Selected:</strong>{" "}
          {projects.find((p) => p._id === selectedProjectId)?.name || "Unknown"}
        </div>
      )}

      {/* Finance - Admin / Head Accountant / Accountant Only */}
      {canViewFinance && (
        <button
          type="button"
          onClick={() => navigate("/Finance")}
          style={{
            ...styles.financeButton,
            ...(isFinanceActive ? styles.financeButtonActive : {}),
          }}
        >
          <span style={styles.financeIcon}>💰</span>
          <span>Finance</span>
        </button>
      )}

      {/* Projects Heading */}
      <h2 style={styles.title}>
        {showArchived
          ? isBeforeBooking
            ? "Archived Projects"
            : "Closed / Archived Projects"
          : "Projects"}
      </h2>

      {/* Archive / Closed Toggle */}
      <div style={styles.toggleContainer}>
        <button
          onClick={() => setShowArchived((prev) => !prev)}
          style={styles.toggleButton}
        >
          {showArchived
            ? "🔙 Back to Active Projects"
            : isBeforeBooking
              ? "📦 View Archived Projects"
              : "📦 View Closed Projects"}
        </button>
      </div>

      {/* Project List */}
      <div style={styles.scrollArea}>
        <ul style={styles.list}>
          {filteredProjects.length > 0 ? (
            filteredProjects.map((project) => (
              <li
                key={project._id}
                style={{
                  ...styles.listItem,

                  backgroundColor:
                    selectedProjectId === project._id
                      ? "#d1e7dd"
                      : hoveredId === project._id
                        ? "#f0f0f0"
                        : "#fff",

                  borderLeft:
                    selectedProjectId === project._id
                      ? "4px solid #0f5132"
                      : "4px solid transparent",
                }}
                onClick={() => handleProjectSelect(project)}
                onMouseEnter={() => setHoveredId(project._id)}
                onMouseLeave={() => setHoveredId(null)}
              >
                {project.name}
              </li>
            ))
          ) : (
            <li style={styles.noProject}>No Project Found</li>
          )}
        </ul>
      </div>
    </div>
  );
};

const styles = {
  sidebar: {
    width: "250px",
    background: "#f4f4f4",
    padding: "20px",
    position: "fixed",
    top: "60px",
    bottom: "0",
    left: "0",
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box",
  },

  searchContainer: {
    position: "relative",
    marginBottom: "15px",
  },

  searchInput: {
    width: "100%",
    padding: "9px 36px 9px 10px",
    borderRadius: "8px",
    border: "1px solid #ccc",
    fontSize: "14px",
    boxSizing: "border-box",
    outline: "none",
    background: "#fff",
  },

  searchIcon: {
    position: "absolute",
    right: "12px",
    top: "50%",
    transform: "translateY(-50%)",
    fontSize: "16px",
    color: "#4a4a4a",
    pointerEvents: "none",
  },

  addContainer: {
    marginBottom: "10px",
  },

  input: {
    width: "100%",
    padding: "8px",
    marginTop: "5px",
    borderRadius: "5px",
    border: "1px solid #ccc",
    boxSizing: "border-box",
  },

  addButton: {
    width: "100%",
    padding: "8px 12px",
    background: "#1d4ed8",
    color: "#fff",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    marginTop: "7px",
    fontWeight: "600",
  },

  selectedProject: {
    background: "#fff3cd",
    color: "#856404",
    padding: "10px",
    marginBottom: "10px",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "500",
    border: "1px solid #ffeeba",
  },

  financeButton: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    gap: "9px",
    padding: "11px 12px",
    marginTop: "2px",
    marginBottom: "8px",
    background: "#ffffff",
    color: "#333",
    border: "1px solid #d6d6d6",
    borderRadius: "6px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    textAlign: "left",
    boxSizing: "border-box",
  },

  financeButtonActive: {
    background: "#e8f1ff",
    color: "#174ea6",
    border: "1px solid #b8d1f5",
  },

  financeIcon: {
    fontSize: "16px",
  },

  title: {
    textAlign: "center",
    fontSize: "20px",
    fontWeight: "bold",
    color: "#5F259F",
    marginTop: "8px",
    marginBottom: "10px",
  },

  toggleContainer: {
    textAlign: "center",
    marginBottom: "10px",
  },

  toggleButton: {
    fontSize: "12px",
    color: "#007bff",
    background: "none",
    border: "none",
    cursor: "pointer",
    textDecoration: "underline",
  },

  scrollArea: {
    flex: 1,
    overflowY: "auto",
    minHeight: 0,
  },

  list: {
    listStyle: "none",
    padding: 0,
    margin: 0,
  },

  listItem: {
    padding: "10px",
    background: "#fff",
    cursor: "pointer",
    borderRadius: "5px",
    marginBottom: "5px",
    transition: "background 0.2s ease",
    boxSizing: "border-box",
    lineHeight: "1.45",
  },

  noProject: {
    padding: "10px",
    color: "gray",
    textAlign: "center",
  },
};

export default Sidebar;
